/**
 * The one place the desk formats dates, money and numbers.
 *
 * Global desk conventions (this is global-lexvio.ai — the India desk's copy
 * of this file is en-IN / Asia/Kolkata / ₹, and none of that may reach a
 * global org, CLAUDE.md §1A):
 *
 *   - Locale: the viewer's own (`undefined` → the browser's), so a US reader
 *     sees "Oct 3, 2026" and a UK reader "3 Oct 2026". Same convention as
 *     `lib/todo/helpers.ts` and the matters/obligations pages.
 *   - Time zone: the browser's own zone (`DESK_TIME_ZONE`), UTC only when the
 *     runtime cannot say. There is no one zone to assume for this product.
 *     Pass the user's own zone (`timeZone`) where a caller knows it.
 *   - Currency: never assumed. There is no `DESK_CURRENCY` constant here on
 *     purpose — the currency of money a person enters is the org's
 *     (`useOrgCurrency()` from `@/lib/auth`), and a figure recorded on a row
 *     is shown in the currency the row states.
 *
 * Every function answers "—" for null, undefined, empty or unparseable input
 * rather than "Invalid Date" or "NaN".
 *
 * Rules that are not style:
 *   - `formatMoney(1000, null)` is a bare number; a currency is never assumed.
 *   - A date-only string (`2026-10-03`) is a calendar date, not an instant:
 *     it is shown as that date in every zone and never gains or loses a day.
 */

/** The viewer's own locale. `undefined` makes `Intl` use the browser's. */
export const DESK_LOCALE: string | undefined = undefined;

/** The browser's zone; UTC only when the runtime cannot say. */
export const DESK_TIME_ZONE: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

const EMPTY = '—';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

type DateInput = string | number | Date | null | undefined;

interface ParsedDate {
  date: Date;
  /** True for a YYYY-MM-DD string: format in UTC so the day never moves. */
  calendar: boolean;
}

function parseDate(value: DateInput): ParsedDate | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number) as [number, number, number];
    const date = new Date(Date.UTC(y, m - 1, d));
    return Number.isNaN(date.getTime()) ? null : { date, calendar: true };
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : { date, calendar: false };
}

export interface DateFormatOptions {
  /** IANA zone; defaults to the browser's. Pass the user's zone where known. */
  timeZone?: string;
  locale?: string;
  /** Override the parts, e.g. `{ day: '2-digit', month: 'short', year: 'numeric' }`. */
  parts?: Intl.DateTimeFormatOptions;
}

/** "Oct 3, 2026" / "3 Oct 2026", by the viewer's locale. */
export function formatDate(value: DateInput, options: DateFormatOptions = {}): string {
  const p = parseDate(value);
  if (!p) return EMPTY;
  return p.date.toLocaleDateString(options.locale ?? DESK_LOCALE, {
    ...(options.parts ?? { day: 'numeric', month: 'short', year: 'numeric' }),
    timeZone: p.calendar ? 'UTC' : (options.timeZone ?? DESK_TIME_ZONE),
  });
}

/** "Oct 3, 2026, 2:30 PM". A date-only input has no time, so it gets none. */
export function formatDateTime(value: DateInput, options: DateFormatOptions = {}): string {
  const p = parseDate(value);
  if (!p) return EMPTY;
  if (p.calendar) return formatDate(value, options);
  return p.date.toLocaleString(options.locale ?? DESK_LOCALE, {
    ...(options.parts ?? {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    timeZone: options.timeZone ?? DESK_TIME_ZONE,
  });
}

/** "2:30 PM". */
export function formatTime(value: DateInput, options: DateFormatOptions = {}): string {
  const p = parseDate(value);
  if (!p || p.calendar) return EMPTY;
  return p.date.toLocaleTimeString(options.locale ?? DESK_LOCALE, {
    ...(options.parts ?? { hour: 'numeric', minute: '2-digit' }),
    timeZone: options.timeZone ?? DESK_TIME_ZONE,
  });
}

type NumberInput = string | number | null | undefined;

function toNumber(value: NumberInput): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface NumberFormatOptions {
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/** "1,234,567" in the viewer's grouping. */
export function formatNumber(value: NumberInput, options: NumberFormatOptions = {}): string {
  const n = toNumber(value);
  if (n === null)
    return value === null || value === undefined || value === '' ? EMPTY : String(value);
  return n.toLocaleString(options.locale ?? DESK_LOCALE, {
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });
}

/** "42%" from 42 (not from 0.42). */
export function formatPercent(value: NumberInput, options: NumberFormatOptions = {}): string {
  const n = toNumber(value);
  if (n === null) return EMPTY;
  return `${formatNumber(n, { maximumFractionDigits: 0, ...options })}%`;
}

/**
 * An amount in the currency the row states: `formatMoney(1000, 'USD')` →
 * "$1,000.00". With no currency the number is shown bare — never assumed.
 * An unparseable amount is shown as given.
 */
export function formatMoney(
  amount: NumberInput,
  currency: string | null | undefined,
  options: NumberFormatOptions = {},
): string {
  if (amount === null || amount === undefined || amount === '') return EMPTY;
  const n = toNumber(amount);
  if (n === null) return String(amount);
  const locale = options.locale ?? DESK_LOCALE;
  const ccy = (currency ?? '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(ccy)) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: ccy,
        // Older engines throw when max < the currency's default min (2).
        minimumFractionDigits:
          options.minimumFractionDigits ??
          (options.maximumFractionDigits !== undefined && options.maximumFractionDigits < 2
            ? options.maximumFractionDigits
            : undefined),
        maximumFractionDigits: options.maximumFractionDigits ?? 2,
      }).format(n);
    } catch {
      return `${ccy} ${n.toLocaleString(locale)}`;
    }
  }
  return n.toLocaleString(locale, {
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
  });
}

/**
 * Short form in the stated currency: "$12.5M", "£1.2K" (compact notation in
 * the viewer's locale). This replaces the India desk's `formatCompactINR`
 * (lakh/crore), which has no place on the global desk. With no currency, a
 * bare compact number.
 */
export function formatCompactMoney(
  amount: NumberInput,
  currency: string | null | undefined,
  options: { locale?: string; maximumFractionDigits?: number } = {},
): string {
  const n = toNumber(amount);
  if (n === null) return EMPTY;
  const locale = options.locale ?? DESK_LOCALE;
  const ccy = (currency ?? '').trim().toUpperCase();
  const base: Intl.NumberFormatOptions = {
    notation: 'compact',
    maximumFractionDigits: options.maximumFractionDigits ?? 1,
  };
  try {
    return new Intl.NumberFormat(
      locale,
      /^[A-Z]{3}$/.test(ccy) ? { ...base, style: 'currency', currency: ccy } : base,
    ).format(n);
  } catch {
    return formatMoney(n, ccy || null, { locale });
  }
}
